import { isNetworkError } from './is-network-error';

export interface ShouldSendToSentryOptions {
  /**
   * HTTP status codes the caller wants filtered out, on top of the defaults
   * (5xx, 401, abort, network errors). Useful when the Qonto backend uses a
   * status semantically — e.g. 405 is returned as a permission/role gate at
   * several endpoints rather than a true verb mismatch.
   */
  ignoreStatuses?: readonly number[];
}

export function shouldSendToSentry(error: unknown, options?: ShouldSendToSentryOptions): boolean {
  // Don't send 5xx, 401, caller-ignored statuses, abort errors, or network errors to Sentry
  if (error && typeof error === 'object') {
    // oxlint-disable-next-line typescript/consistent-type-assertions
    const status = 'status' in error ? (error.status as number) : undefined;

    // Caller-specified statuses to ignore
    if (typeof status === 'number' && options?.ignoreStatuses?.includes(status)) {
      return false;
    }

    // Check for 5xx errors
    if (status && status >= 500 && status < 600) {
      return false;
    }

    // Check for 401 errors
    if (status === 401) {
      return false;
    }

    // Check for abort errors
    if (error instanceof DOMException && error.name === 'AbortError') {
      return false;
    }

    // Check for network errors
    if (isNetworkError(error)) {
      return false;
    }
  }

  return true;
}
